<template>
  <div class="feedback-buttons">
    <ButtonComponent
      class="button"
      :icon="feedback === 'positive' ? 'general.likeSolid' : 'general.like'"
      icon-size="xs"
      tooltip="Like"
      :class="{ selected: feedback === 'positive', like: true, 'not-clickable': feedback === 'positive' }"
      @click="giveFeedback('positive')"
      :style="feedback === 'positive' ? 'pointer-events: none;' : ''"
      data-testid="feedback-like"
    />
    <ButtonComponent
      class="button"
      :icon="feedback === 'negative' ? 'general.dislikeSolid' : 'general.dislike'"
      icon-size="xs"
      tooltip="Dislike"
      :class="{ selected: feedback === 'negative', dislike: true, 'not-clickable': feedback === 'negative' }"
      @click="giveFeedback('negative')"
      :style="feedback === 'negative' ? 'pointer-events: none;' : ''"
      data-testid="feedback-dislike"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { ButtonComponent } from '@/storybook';
import { useTrack, TrackEvents } from '@/composables/useTrack';
import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { useSmartActionsStore } from '@/modules/viewer/store/useSmartActionsStore';
import { storeToRefs } from 'pinia';

const props = defineProps<{ feedback: null | 'positive' | 'negative' }>();
const emit = defineEmits<{
  (e: 'feedback', type: 'positive' | 'negative' | null): void;
}>();

const { trackItemOwner } = useTrack();
const itemStore = useViewerItemStore();
const smartActionsStore = useSmartActionsStore();
const { id: itemIdRaw } = storeToRefs(itemStore);
const { actionType: actionTypeRaw } = storeToRefs(smartActionsStore);
const itemId = itemIdRaw ?? ref('');
const actionType = actionTypeRaw ?? ref('');

function giveFeedback(type: 'positive' | 'negative') {
  if (props.feedback === type) return; // Prevent re-clicking selected
  emit('feedback', type);
  trackItemOwner(TrackEvents.SMARTACTIONS_FEEDBACK, {
    type,
    item_id: itemId.value,
    smart_action_type: actionType.value,
  });
}

// Reset feedback when actionType changes (new smart action generated)
watch(actionType, () => {
  emit('feedback', null);
});
</script>

<style scoped>
.feedback-buttons {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}
.button {
  color: var(--slate-400);
  transition: color 0.2s, background 0.2s;
  border-radius: 50%;
  padding: 0.25rem;
  cursor: pointer;
}
.button:hover {
  background: var(--slate-100);
}
.button.selected.like {
  color: var(--green-500);
  background: var(--green-50);
}
.button.selected.dislike {
  color: var(--red-500);
  background: var(--red-50);
}
.button.not-clickable {
  cursor: default !important;
  pointer-events: none;
  background: inherit;
}
</style>
